import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs))
}

export function formatUrlForDisplay(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "")
}

export function normalizeUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ""
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
    return trimmed
  }
  return `https://${trimmed}`
}

/**
 * Checks if a string is a valid email address
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

export function isMobileApp() {
  return typeof window !== "undefined" && Boolean(window.isMobileApp)
}

export function isAndroidApp() {
  return isMobileApp() && /android/i.test(navigator.userAgent)
}

export function isIOSApp() {
  return isMobileApp() && /iphone|ipad|ipod/i.test(navigator.userAgent)
}

export function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ""
}

interface FetchOptions extends Omit<RequestInit, "body" | "headers"> {
  body?: Record<string, unknown>
  headers?: Record<string, string>
}

export interface ApiError {
  status: number
  errors?: Record<string, string[]>
  message?: string
}

export class ApiRequestError extends Error {
  status: number
  errors?: Record<string, string[]>

  constructor({ status, errors, message }: ApiError) {
    super(message || `Request failed: ${status}`)
    this.name = "ApiRequestError"
    this.status = status
    this.errors = errors
  }

  /** Returns flattened errors for form display (first error message per field) */
  getFormErrors(): Record<string, string> {
    if (!this.errors) return {}
    const result: Record<string, string> = {}
    for (const [key, messages] of Object.entries(this.errors)) {
      result[key] = Array.isArray(messages) ? messages[0] : String(messages)
    }
    return result
  }
}

/**
 * Fetch wrapper that automatically handles CSRF token and JSON serialization.
 * Use this for non-Inertia requests that shouldn't trigger page re-renders.
 */
export async function apiFetch<T = unknown>(
  url: string,
  { body, headers, ...options }: FetchOptions = {}
): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    // Try to parse error response as JSON
    const contentType = response.headers.get("content-type")
    if (contentType?.includes("application/json")) {
      const errorData = await response.json()
      throw new ApiRequestError({
        status: response.status,
        errors: errorData.errors,
        message: errorData.message || errorData.error,
      })
    }
    throw new ApiRequestError({ status: response.status })
  }

  // Handle empty responses (e.g., 201 Created, 204 No Content)
  const contentLength = response.headers.get("content-length")
  if (contentLength === "0" || response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get("content-type")
  if (!contentType?.includes("application/json")) {
    throw new Error(`Expected JSON response but received content-type: ${contentType ?? "none"}`)
  }

  return response.json()
}

/**
 * Extract conversation ID from a URL like /chat/conversations/123
 */
export function extractConversationId(url: string): number | null {
  const match = url.match(/\/conversations\/(\d+)/)
  return match ? Number.parseInt(match[1], 10) : null
}

interface SubmitFormOptions {
  method?: "POST" | "PATCH" | "PUT" | "DELETE"
}

/**
 * Submit a form via fetch with multipart/form-data support.
 * Use this for non-Inertia form submissions (e.g., modal-to-modal navigation).
 * Supports file uploads automatically via FormData.
 */
export async function submitForm<T = unknown>(
  url: string,
  formData: FormData,
  { method = "POST" }: SubmitFormOptions = {}
): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": getCsrfToken(),
      // Note: Do NOT set Content-Type - browser sets it with boundary for multipart
    },
    body: formData,
  })

  if (!response.ok) {
    const contentType = response.headers.get("content-type")
    if (contentType?.includes("application/json")) {
      const errorData = await response.json()
      throw new ApiRequestError({
        status: response.status,
        errors: errorData.errors,
        message: errorData.message || errorData.error,
      })
    }
    throw new ApiRequestError({ status: response.status })
  }

  // Handle empty responses
  const contentLength = response.headers.get("content-length")
  if (contentLength === "0" || response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get("content-type")
  if (!contentType?.includes("application/json")) {
    throw new Error(`Expected JSON response but received content-type: ${contentType ?? "none"}`)
  }

  return response.json()
}
